import { Option } from '@inrev/core';
import { isMatching, P } from 'ts-pattern';
import * as z from 'zod';
import { suretyTypeSchema } from '../../../../enums.mjs';
import { isoDateTimeSchema, suretyAccountBondingLineId, bondingLinesVersion } from '../../../../types/opaque.mjs';
import { maskedLineSchema, limitsSchema, linesSchema } from '../../../../types/surety/account/bonding-line.mjs';
import { currencySchema, formatToDollar } from '../../../../types/utils.mjs';
import { entriesBySuretyType } from '../../../../utils/by-surety-type.util.mjs';

const BONDING_LINES_MEDIA_TYPE = "application/vnd.inrev.bonding-lines+json";
var Get;
(function (Get) {
    (function (Response) {
        Response.lineWithUsageSchema = z.object({
            name: z.string(),
            used: currencySchema,
            line: maskedLineSchema
                .extend({ id: suretyAccountBondingLineId, updatedAt: isoDateTimeSchema })
                .nullable(),
        });
        Response.schema = z.object({
            published: z.boolean(),
            limits: limitsSchema.nullable(),
            used: currencySchema,
            lines: z.record(suretyTypeSchema, Response.lineWithUsageSchema),
        });
    })(Get.Response || (Get.Response = {}));
})(Get || (Get = {}));
const lineRequiredMessage = "Add a line to save";
const publishedNeedsPlaceableLineMessage = "Published bonding lines need one with limits above $0. Keep one, or unpublish first.";
const limitFieldNames = { singleLimit: "Single", aggregateLimit: "Aggregate" };
const lineExceedsLimitsMessage = (field, limit) => `${limitFieldNames[field]} can't exceed account limits (${formatToDollar(limit)})`;
const hasLinesAndLimits = isMatching({ lines: P.nonNullable, limits: P.nonNullable });
var Create;
(function (Create) {
    (function (Request) {
        Request.schema = z
            .object({
            lines: linesSchema.refine((lines) => suretyTypeSchema.options.some((suretyType) => Option.isSome(lines[suretyType])), { error: lineRequiredMessage }),
            limits: limitsSchema,
            version: bondingLinesVersion,
        })
            .superRefine(({ lines, limits }, ctx) => {
            entriesBySuretyType(lines).forEach(([suretyType, line]) => {
                limitsSchema
                    .keyof()
                    .options.filter((field) => line[field] > limits[field])
                    .forEach((field) => {
                    ctx.addIssue({
                        code: "custom",
                        path: ["lines", suretyType, field],
                        message: lineExceedsLimitsMessage(field, limits[field]),
                    });
                });
            });
        }, { when: ({ value }) => hasLinesAndLimits(value) });
    })(Create.Request || (Create.Request = {}));
})(Create || (Create = {}));
var Publish;
(function (Publish) {
    (function (Request) {
        Request.schema = z.object({ version: bondingLinesVersion });
    })(Publish.Request || (Publish.Request = {}));
})(Publish || (Publish = {}));
var ErrorDetails;
(function (ErrorDetails) {
    (function (BondingLineOmitted) {
        BondingLineOmitted.schema = z.object({ suretyType: suretyTypeSchema });
    })(ErrorDetails.BondingLineOmitted || (ErrorDetails.BondingLineOmitted = {}));
})(ErrorDetails || (ErrorDetails = {}));

export { BONDING_LINES_MEDIA_TYPE, Create, ErrorDetails, Get, Publish, publishedNeedsPlaceableLineMessage };
